import { useCallback, useMemo } from 'react';

import { useQueryStates } from 'nuqs';

import { patternLiveFiltersParser } from '@/shared/lib/url-parsers';

import {
    useLastUpdatedTime,
    usePinnedUniqueId,
} from '../model/selectors/pattern-live.selectors';
import type { SearchLivePatternsParams } from '../model/types/pattern-live';

export const usePatternLiveFilters = () => {
    const [urlFilters, setUrlFilters] = useQueryStates(
        patternLiveFiltersParser,
        {
            clearOnDefault: false,
            history: 'replace',
        },
    );

    const lastUpdated = useLastUpdatedTime();
    const pinnedPattern = usePinnedUniqueId();

    const buildSearchParams = useCallback(() => {
        /* eslint-disable @typescript-eslint/naming-convention */
        const {
            ticker,
            timeframe,
            threshold,
            unique_id,
            perc_min_rialzista,
            perc_max_rialzista,
            perc_min_ribassista,
            perc_max_ribassista,
            sort_by,
            sort_order,
            limit,
            offset,
        } = urlFilters;
        /* eslint-enable @typescript-eslint/naming-convention */

        return {
            ...(ticker?.length && { ticker }),
            ...(timeframe?.length && { timeframe }),
            ...(threshold?.length && { threshold }),
            ...(unique_id && { unique_id }),
            ...(perc_min_rialzista != null && { perc_min_rialzista }),
            ...(perc_max_rialzista != null && { perc_max_rialzista }),
            ...(perc_min_ribassista != null && { perc_min_ribassista }),
            ...(perc_max_ribassista != null && { perc_max_ribassista }),
            ...(sort_by && { sort_by }),
            ...(sort_order && { sort_order: sort_order as 'asc' | 'desc' }),
            ...(limit != null && { limit }),
            ...(offset != null && { offset }),
        } as SearchLivePatternsParams;
    }, [urlFilters]);

    const filters = useMemo(() => buildSearchParams(), [buildSearchParams]);

    // If there's a pinned pattern, ensure it's included in the filters
    const apiFilters = useMemo(() => {
        // Always remove unique_id from URL filters first
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { unique_id, ...filtersWithoutUniqueId } = filters;

        // Only add unique_id if we have a pinned pattern
        if (pinnedPattern) {
            return { ...filtersWithoutUniqueId, unique_id: pinnedPattern };
        }

        // No pinned pattern: return filters without unique_id
        return filtersWithoutUniqueId;
    }, [filters, pinnedPattern]);

    const setFilters = useCallback(
        (newFilters: Partial<SearchLivePatternsParams>) => {
            setUrlFilters({
                ticker: newFilters.ticker ?? null,
                timeframe: newFilters.timeframe ?? null,
                threshold: newFilters.threshold ?? null,
                unique_id: newFilters.unique_id ?? null,
                perc_min_rialzista: newFilters.perc_min_rialzista ?? null,
                perc_max_rialzista: newFilters.perc_max_rialzista ?? null,
                perc_min_ribassista: newFilters.perc_min_ribassista ?? null,
                perc_max_ribassista: newFilters.perc_max_ribassista ?? null,
                sort_by: newFilters.sort_by ?? null,
                sort_order: newFilters.sort_order ?? null,
                limit: newFilters.limit ?? null,
                offset: newFilters.offset ?? null,
            });
        },
        [setUrlFilters],
    );

    const updateFilters = useCallback(
        (partialFilters: Partial<SearchLivePatternsParams>) => {
            setFilters({ ...filters, ...partialFilters });
        },
        [setFilters, filters],
    );

    const clearFilters = useCallback(() => {
        setUrlFilters({
            ticker: null,
            timeframe: null,
            threshold: null,
            unique_id: null,
            perc_min_rialzista: null,
            perc_max_rialzista: null,
            perc_min_ribassista: null,
            perc_max_ribassista: null,
            sort_by: null,
            sort_order: null,
            limit: null,
            offset: null,
        });
    }, [setUrlFilters]);

    return {
        filters,
        setFilters,
        apiFilters,
        updateFilters,
        clearFilters,
        lastUpdated,
    };
};
